import type { ObjectId } from "@novorender/api";

import type { ObjectDataAccess, SelectionResolutionMode, SelectionTarget } from "dataAccess";
import { telemetry } from "telemetry";

import { SelectionResolution } from "./types";

/**
 * The click handler's side of selection resolution: measure it, and never let it fail a
 * click. The walk itself belongs to `ObjectDataAccess`.
 *
 * The event lives here so there is one of it — an implementation emitting it would give
 * three copies under a `render.` name it has no business owning.
 */

/** The persisted setting's values, as the interface spells them. */
const RESOLUTION_MODES: Record<SelectionResolution, SelectionResolutionMode> = {
    [SelectionResolution.FirstObject]: "first-object",
    [SelectionResolution.LastObject]: "last-object",
};

/** What a click selects, with the picked object as the floor when resolution cannot answer. */
export async function resolveSelectionTarget({
    dataAccess,
    objectId,
    resolution,
    classAware,
    abortSignal,
}: {
    dataAccess: ObjectDataAccess;
    objectId: ObjectId;
    resolution: SelectionResolution;
    /** Whether class-aware (`Procore/NodeClass`) resolution is on — a host feature flag. */
    classAware: boolean;
    abortSignal: AbortSignal;
}): Promise<SelectionTarget> {
    const startedAt = performance.now();
    const single = (): SelectionTarget => ({
        mainObjectId: objectId,
        highlightIds: new Uint32Array([objectId]),
        outcome: "legacy",
    });

    let result: SelectionTarget;
    let outcome: SelectionTarget["outcome"] | "unresolved" | "error-fallback";

    try {
        const resolved = await dataAccess.resolveSelection(objectId, {
            mode: RESOLUTION_MODES[resolution],
            classAware,
            signal: abortSignal,
        });
        if (!resolved) {
            if (abortSignal.aborted) {
                // A newer pick superseded this one and the caller discards the result after
                // its own `aborted` check — not an outcome worth an event.
                return single();
            }
            result = single();
            outcome = "unresolved";
        } else {
            result = resolved;
            outcome = resolved.outcome;
        }
    } catch (error) {
        if (abortSignal.aborted) {
            return single();
        }
        console.warn("Error resolving selection target:", error);
        telemetry.trackCaughtException({ source: "render.selectionResolution", category: "render", error });
        result = single();
        outcome = "error-fallback";
    }

    telemetry.trackEvent("render.selection_resolution", {
        "selection.mode": resolution,
        "selection.class_aware": classAware,
        "selection.backend": dataAccess.kind,
        "selection.outcome": outcome,
        "selection.target_changed": result.mainObjectId !== objectId,
        "selection.highlight_count": result.highlightIds.length,
        "selection.elapsed_ms": performance.now() - startedAt,
    });
    return result;
}
